/**
 * BookPlanDialog — customer books an approved plan.
 *
 * Reused by the trip builder and the My Requests page (a plan is only bookable
 * once the team has approved its request). Creates a booking via
 * plansService.bookPlan and shows a confirmation with the reference.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Send, Groups, CheckCircle } from '@mui/icons-material';
import plansService from '../../services/plansService';
import ButtonPill from '../ui/ButtonPill';

const tomorrowIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const BookPlanDialog = ({ open, onClose, planId, defaultTravelers = 2, defaultTravelDate = '', onBooked }) => {
  const navigate = useNavigate();
  const minDate = tomorrowIso();

  const [form, setForm] = useState({
    travel_date: defaultTravelDate && defaultTravelDate >= minDate ? defaultTravelDate : '',
    num_travelers: defaultTravelers || 1,
    customer_phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!form.travel_date) return setError('Please choose a travel date.');
    if (form.travel_date < minDate) return setError('Travel date must be in the future.');
    try {
      setSubmitting(true);
      const res = await plansService.bookPlan(planId, {
        travel_date: form.travel_date,
        num_travelers: parseInt(form.num_travelers, 10) || 1,
        customer_phone: form.customer_phone || undefined,
      });
      setBooking(res?.booking ?? {});
      onBooked?.(res?.booking);
    } catch (err) {
      setError(err?.message ?? 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setBooking(null);
    setError('');
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      {booking ? (
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>You're booked!</Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            Confirmation <strong>{booking.reference}</strong> is on its way to your email.
          </Typography>
          {booking.total_price != null && (
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Total: {booking.currency || ''} {Number(booking.total_price).toLocaleString()}
            </Typography>
          )}
          <ButtonPill variant="contained" onClick={() => { close(); navigate('/user/bookings'); }}>
            View my bookings
          </ButtonPill>
        </DialogContent>
      ) : (
        <>
          <DialogTitle sx={{ fontWeight: 700 }}>Book this trip</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your plan is approved. Confirm the details below to book it.
            </Typography>
            {error && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>
            )}
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Travel date"
                  type="date"
                  value={form.travel_date}
                  onChange={(e) => setForm((f) => ({ ...f, travel_date: e.target.value }))}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDate }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Travelers"
                  type="number"
                  value={form.num_travelers}
                  onChange={(e) => setForm((f) => ({ ...f, num_travelers: e.target.value }))}
                  fullWidth
                  inputProps={{ min: 1 }}
                  InputProps={{ startAdornment: <Groups sx={{ fontSize: 20, mr: 0.75, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone (optional)"
                  value={form.customer_phone}
                  onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
                  fullWidth
                  placeholder="So our team can reach you about this trip"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={close} disabled={submitting} sx={{ borderRadius: 9999 }}>Cancel</Button>
            <ButtonPill
              variant="contained"
              onClick={submit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : <Send />}
            >
              {submitting ? 'Booking...' : 'Confirm booking'}
            </ButtonPill>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default BookPlanDialog;
